import {
	useEffect,
	useMemo,
	useState,
	type ChangeEvent,
	type FormEvent,
} from 'react'

import { ErrorList } from '#app/components/form/error-list.tsx'
import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import {
	COMPLAINT_SYSTEM_USAGE_DESCRIPTIONS,
	PLATFORM_AUTOFILL_LABELS,
	toAutofillPlatformValue,
} from '#app/routes/mocra+/_form+/adverse-events/helpers.ts'
import { AE_SYSTEM_COPY } from '#app/routes/mocra+/_form+/components/ae-systems/constants.ts'
import {
	createAeSystemDraft,
	isAeSystemUsageValue,
	sanitizeAeSystemDraft,
	type AeSystemDraft,
} from '#app/routes/mocra+/_form+/components/ae-systems/types.ts'
import {
	COMPLAINT_SYSTEM_PLATFORM_OPTIONS,
	COMPLAINT_SYSTEM_USAGE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'
import { cn } from '#app/utils/misc.tsx'

type Mode = 'add' | 'edit'

type DraftErrors = Partial<
	Pick<AeSystemDraft, 'name'> & {
		usage?: string
	}
>

type Props = {
	mode: Mode
	open: boolean
	onOpenChange: (open: boolean) => void
	onSubmit: (draft: AeSystemDraft) => void
	initialValue?: AeSystemDraft
	onCancel?: () => void
	onClosed?: () => void
}

export function AeSystemOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<AeSystemDraft>(
		initialValue ? { ...initialValue } : createAeSystemDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createAeSystemDraft())
			setErrors({})
		} else if (!open) {
			onClosed?.()
		}
	}, [initialValue, onClosed, open])

	const titleCopy =
		mode === 'add' ? AE_SYSTEM_COPY.title.add : AE_SYSTEM_COPY.title.edit
	const submitCopy =
		mode === 'add' ? AE_SYSTEM_COPY.submit.add : AE_SYSTEM_COPY.submit.edit
	const descriptionCopy = AE_SYSTEM_COPY.description

	const platformOptions = useMemo(() => COMPLAINT_SYSTEM_PLATFORM_OPTIONS, [])
	const usageOptions = useMemo(() => COMPLAINT_SYSTEM_USAGE_OPTIONS, [])

	const handlePlatformChange = (event: ChangeEvent<HTMLSelectElement>) => {
		const nextPlatform = event.target.value as AeSystemDraft['platform']
		const previousPlatformValue = toAutofillPlatformValue(draft.platform)
		const previousLabel = previousPlatformValue
			? PLATFORM_AUTOFILL_LABELS.get(previousPlatformValue)
			: undefined
		const currentName = draft.name.trim()

		setDraft((prev) => ({ ...prev, platform: nextPlatform }))

		const nextPlatformValue = toAutofillPlatformValue(nextPlatform)
		const nextLabel = nextPlatformValue
			? PLATFORM_AUTOFILL_LABELS.get(nextPlatformValue)
			: undefined
		if (!nextLabel) {
			if (previousLabel && currentName === previousLabel) {
				setDraft((prev) => ({ ...prev, name: '' }))
			}
			return
		}
		const shouldReplace =
			currentName.length === 0 ||
			(previousLabel && currentName === previousLabel)
		if (shouldReplace) {
			setDraft((prev) => ({ ...prev, name: nextLabel }))
		}
	}

	const toggleUsage = (value: AeSystemDraft['usage'][number]) => {
		setDraft((prev) => {
			const selection = prev.usage
			const exists = selection.includes(value)
			const nextSelection = exists
				? selection.filter((item) => item !== value)
				: [...selection, value]
			return {
				...prev,
				usage: nextSelection,
			}
		})
	}

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		event.stopPropagation()
		const sanitized = sanitizeAeSystemDraft({
			...draft,
			usage: draft.usage.filter(isAeSystemUsageValue),
		})
		const nextErrors: DraftErrors = {}
		if (!sanitized.name) {
			nextErrors.name = AE_SYSTEM_COPY.warnings.nameMissing
		}
		if (sanitized.usage.length === 0) {
			nextErrors.usage = AE_SYSTEM_COPY.warnings.usageMissing
		}
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
		onOpenChange(false)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={descriptionCopy}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="ae-system-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<form id="ae-system-overlay-form" onSubmit={handleSubmit}>
				<SelectField
					labelProps={{ children: AE_SYSTEM_COPY.fields.platformLabel }}
					selectProps={{
						value: draft.platform,
						onChange: handlePlatformChange,
						name: 'platform',
					}}
					options={platformOptions}
				/>
				<Field
					labelProps={{ children: AE_SYSTEM_COPY.fields.nameLabel }}
					inputProps={{
						value: draft.name,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({ ...prev, name: event.target.value })),
						type: 'text',
						name: 'name',
					}}
					errors={errors.name ? [errors.name] : undefined}
				/>
				<div className="space-y-2">
					<p className="text-sm font-medium">
						{AE_SYSTEM_COPY.fields.usageLabel}
					</p>
					<div className="space-y-3">
						{usageOptions.map((option) => {
							const isSelected = draft.usage.includes(option.value)
							return (
								<label
									key={option.value}
									className={cn(
										'border-input hover:border-primary/60 hover:bg-primary/5 flex gap-3 rounded-md border p-3 transition',
										isSelected && 'border-primary bg-primary/5',
									)}
								>
									<input
										type="checkbox"
										className="border-input accent-foreground focus-visible:ring-ring mt-1 h-4 w-4 border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden"
										checked={isSelected}
										onChange={() => toggleUsage(option.value)}
									/>
									<div className="space-y-1">
										<p className="text-sm font-medium">{option.label}</p>
										<p className="text-muted-foreground text-xs">
											{COMPLAINT_SYSTEM_USAGE_DESCRIPTIONS[option.value]}
										</p>
									</div>
								</label>
							)
						})}
					</div>
					<div className="min-h-[16px]">
						{errors.usage ? (
							<ErrorList id="ae-system-usage-error" errors={[errors.usage]} />
						) : null}
					</div>
				</div>
			</form>
		</ResponsiveOverlay>
	)
}
